/**
 * ExecutorEstateView — read-only estate plan view for tier-2 executors.
 * Used at /executor/:clientId/estate (both pre-death and post-death).
 *
 * Fetches the assigned client's data and renders a read-only Dashboard
 * with a clear "read-only" banner at the top.
 */

import { useQuery } from "@tanstack/react-query";
import { useDeathEvent } from "@/lib/use-death-event";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, ArrowLeft } from "lucide-react";
import Dashboard from "@/pages/Dashboard";
import type { Client } from "@shared/schema";

interface ExecutorEstateViewProps {
  clientId: string;
}

export default function ExecutorEstateView({ clientId }: ExecutorEstateViewProps) {
  const [, setLocation] = useLocation();

  // Fetch the assigned client's data directly
  const { data: clientData, isLoading } = useQuery<Client | null>({
    queryKey: ["/api/clients/as-executor", clientId],
    queryFn: async () => {
      // The /api/clients endpoint returns the user's own client records.
      // For executor access, we fetch the specific client by their ID.
      const res = await fetch(`/api/clients/${clientId}`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!clientId,
  });

  // Fetch planner name + death status from contexts
  const { data: contexts } = useQuery<{ myEstatePlan: any; executorFor: any[] }>({
    queryKey: ["/api/user/contexts"],
  });
  const myContext = contexts?.executorFor?.find((c: any) => c.clientId === clientId);
  const plannerName = myContext?.plannerName ?? clientData?.name ?? "the plan owner";

  // Fetch death event
  const { data: deathEvent } = useDeathEvent(clientId);

  const isPostDeath = deathEvent?.status === "verified";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="flex items-center justify-center p-12 text-center">
        <div>
          <p className="font-medium">Unable to load estate plan</p>
          <p className="text-sm text-muted-foreground mt-1">You may not have access to this estate plan yet.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => setLocation(`/executor/${clientId}/progress`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-0">
      {/* Read-only banner */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-muted/60 border-b text-sm shrink-0">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Eye className="w-4 h-4 shrink-0" />
          <span>
            {isPostDeath
              ? `Viewing ${plannerName}'s estate plan — read-only`
              : `Pre-death access — ${plannerName}'s estate plan — read-only`}
          </span>
          {!isPostDeath && (
            <Badge variant="outline" className="text-xs ml-1">Tier 2</Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation(isPostDeath ? `/executor/${clientId}/dashboard` : `/executor/${clientId}/progress`)}
          className="text-xs h-7 shrink-0"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" />
          Back
        </Button>
      </div>

      {/* Read-only dashboard */}
      <Dashboard clientData={clientData} readOnly />
    </div>
  );
}
